import { FC, useEffect, useState } from "react";
import LmsTable from "../ui/LmsTable";
import { removeNullFromObj, useFetchData, useParseRouteActions } from "../../../helpers/GlobalHelper";
import BCModal from "../../ui/BCModal";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import { GlobalConstants, LmsBuildersCapitalPercentagesMetadataList, LmsPurchasingCompanyMetadataList } from "../../constants/GlobalConstants";
import { FeatureFlags } from "../../constants/LocalServerConstants";
import { purchaseAdviceQuoteViewFields } from "../ui/LmsSharedForms";

interface AddPurchaseAdviceQuoteModalContentProps {
  id: string;
  onClose: () => void;
}

interface PurchaseAdviceQuoteViewProps {
  id: string;
}

const LmsServicingPurchaseAdviceQuotesElegibleLoan = () => {
  const [selectedPurchaseEligible, setSelectedPurchaseEligible] = useState("");
  const handlePurchaseAdviceQuoteEligibleLoanClick = (clickedRow) => {
    if (clickedRow?.id) {
      setSelectedPurchaseEligible(clickedRow.id);
    }
  };

  const handleCloseModal = () => {
    setSelectedPurchaseEligible("");
  };

  return (
    <>
      <BCModal
        size="fullscreen"
        closeIcon
        isModalOpen={!!selectedPurchaseEligible}
        onClose={handleCloseModal}
        content={
          <AddPurchaseAdviceQuoteModalContent id={selectedPurchaseEligible} onClose={handleCloseModal} />
        }
      />
      <LmsTable
        apiSuffix="purchasequotes/purchase-eligible"
        actionButtons={["export"]}
        handleClick={(rows) => handlePurchaseAdviceQuoteEligibleLoanClick(rows.clickedRow)}
        columnLabelsDictionary={{
          borrowerOrganization: "Borrower"
        }}
        columnClickOptions={{
          loan: {
            path: "",
            idProp: "id"
          }
        }}
      />
    </>
  );
};

const AddPurchaseAdviceQuoteModalContent: FC<
  AddPurchaseAdviceQuoteModalContentProps
> = ({ id, onClose }) => {
  const [{ data: purchaseQuoteInitialData, isLoading: isPurchaseQuoteInitialDataLoading, error, fetchData }] = useFetchData(`purchasequotes/initial-data/${id}`); 
  const [dueToBcData, setDueToBcData] = useState(undefined as any);

  const formatSubmitData = (inputs) => {
    delete inputs.revenueRecognitionModalData;
    return {
      ...inputs,
      "loanId": purchaseQuoteInitialData?.loanId,
      "purchaseQuoteLoanTransactionTempId": inputs?.purchaseQuoteLoanTransactionTempId,
      "revenueRecognitionTempId": inputs?.revenueRecognitionTempId,
    };
  };

  const myForm = purchaseAdviceQuoteViewFields(
    new BCFormController({ 
      ...purchaseQuoteInitialData, 
      "dueToBc": dueToBcData,
      "validThroughDate": null
    }, { displayMessageLocally: true, values: { 
      loanId: purchaseQuoteInitialData?.loanId,
      loanComments: purchaseQuoteInitialData?.loanComments,
      purchasersTotalPurchase: purchaseQuoteInitialData?.totalAmount ? purchaseQuoteInitialData?.totalAmount : 0,
      revenueRecognitionTempId: purchaseQuoteInitialData?.revenueRecognitionTempId,
      purchaseQuoteLoanTransactionTempId: purchaseQuoteInitialData?.purchaseQuoteLoanTransactionTempId,
      buildersCapitalPercentageId: purchaseQuoteInitialData?.buildersCapitalPercentageId,
      notionalBuildersCapitalPercentageId: purchaseQuoteInitialData?.notionalBuildersCapitalPercentageId
     } }), 
    true,
    false
  );

  const calculatePurchasersTotalPurchase = async (inputs) => {
    const selectedFundOrganizationId = inputs.fundOrganizationId;
    let totalPurchase;
    if (selectedFundOrganizationId && selectedFundOrganizationId !== "b21bb1f4-b2ff-48ba-8b5d-ae010124d5f8") {
      totalPurchase = purchaseQuoteInitialData?.loanAmountCurrent + purchaseQuoteInitialData?.additionalInterestFloor + inputs?.reconveyanceFee;
      totalPurchase = totalPurchase + (inputs?.additionalPurchaseFee ? inputs?.additionalPurchaseFee : 0);
      totalPurchase = totalPurchase + (inputs?.additionalInterestAmount ? inputs?.additionalInterestAmount : 0);
      totalPurchase = totalPurchase + (inputs?.interestReserveBalance ? inputs?.interestReserveBalance : 0);
      if (inputs?.includeFutureInterest) {
        totalPurchase = totalPurchase + (inputs?.interestPdstartPdend1 ? inputs?.interestPdstartPdend1 : 0) + (inputs?.interestPdstartPdend2 ? inputs?.interestPdstartPdend2 : 0);
      }
      totalPurchase = totalPurchase - (inputs?.unearnedRevenue ? inputs?.unearnedRevenue : 0);
      if (inputs.saleTypeOption === "Sale to purchasing company") {
        inputs.fundTotalPurchase = totalPurchase;
      }
      else {
        inputs.purchasersTotalPurchase = totalPurchase;
      }
    }
  };

  const onAddPurchaseAdviceQuoteFormChanged = async (options, lastUpdated) => {
    const inputCopy = {...options?.inputs};
    if (lastUpdated === "fundOrganizationId" || lastUpdated === "purchaseRequestDate" || lastUpdated === "validThroughDate" || lastUpdated === "additionalInterestRate") {
      calculatePurchasersTotalPurchase(inputCopy);
    }

    if ((lastUpdated === "amortizationOption" || lastUpdated === "purchaseRequestDate" || lastUpdated === "recognitionOption") && inputCopy.revenueRecognitionTempId) {
      inputCopy.showRevenueRegenerateMessage = true;
    }

    if (lastUpdated === "buildersCapitalPercentageId") {
      if (inputCopy.buildersCapitalPercentageId === "fb561aef-624d-4af9-bd2c-a99c007919de") {
        inputCopy.notionalBuildersCapitalPercentageId = "";
      }
    }

    if (lastUpdated === "buildersCapitalPercentageId" 
    || lastUpdated === "purchaseRequestDate" 
    || lastUpdated === "purchaseOrganizationId" 
    || lastUpdated === "buildersCapitalPercentageId" 
    || lastUpdated === "notionalBuildersCapitalPercentageId") {
      const updatedObj = removeNullFromObj({
        id: inputCopy?.id,
        loanId: inputCopy?.loanId,
        purchaseDate: inputCopy?.purchaseRequestDate,
        purchaseOrganizationId: inputCopy?.purchaseOrganizationId,
        buildersCapitalPercentageId: inputCopy?.buildersCapitalPercentageId ? inputCopy?.buildersCapitalPercentageId : "fb561aef-624d-4af9-bd2c-a99c007919de",
        notionalBuildersCapitalPercentageId: inputCopy?.notionalBuildersCapitalPercentageId ? inputCopy?.notionalBuildersCapitalPercentageId : "5ff3f496-366c-431a-9a06-ab6b010e749b",
        purchaseQuoteLoanTransactionTempId: inputCopy?.purchaseQuoteLoanTransactionTempId
      });
      
      await fetchData("purchasequotes/loan-transaction-temp", { fetchMethod: "POST", data: updatedObj, skipCache: true });
      inputCopy.refreshLoanTransactionTable = `${Date.now()}-${lastUpdated}`;
      const selectedPurchasingCompany = LmsPurchasingCompanyMetadataList.find(company => company.id === options?.inputs?.purchaseOrganizationId);
      if (inputCopy.saleTypeOption === "Sale to purchasing company" && selectedPurchasingCompany?.display.includes("Global")) {
        if (inputCopy?.buildersCapitalPercentageId) {
          const selectedBCPercentage = Number(LmsBuildersCapitalPercentagesMetadataList.find(percentage => percentage.id === inputCopy.buildersCapitalPercentageId)?.display);
          inputCopy.purchasersTotalPurchase = (inputCopy.loanAmountCurrent * (100 - selectedBCPercentage)) / 100;
        }
        else {
          inputCopy.purchasersTotalPurchase = 0;
        }
      }
      else {
        await getDueToBcValue(inputCopy);
      }
    }

    options?.setInputs({ ...inputCopy });
  };
  
  const handleGeneratePurchaseAdvice = (inputs, options) => {
    if (!inputs?.revenueRecognitionTempId) {
      options.setFormErrorMessage({ message: "Earned/Unearned Revenue not generated." });
    }
    else if (inputs?.showRevenueRegenerateMessage) {
      options.setFormErrorMessage({ message: "Please regenerate Earned/Unearned Revenue." });
    }
    else {
      options.handleSubmit("purchasequotes/generate-purchase-advice-quote", { 
        method: "POST", 
        data: formatSubmitData(inputs), 
        buttonText: options.buttonText,
        onSuccessFunc: () => history.back()
      });
    }
  };

  const getDueToBcValue = async (obj) => {
    const { data: dueToBcValue } = await fetchData(`purchasequotes/due-to-bc/${obj?.purchaseQuoteLoanTransactionTempId}`, { fetchMethod: "GET" });
    setDueToBcData(dueToBcValue?.dueToBc);
  };

  useEffect(() => {
    if (purchaseQuoteInitialData) {
      if (purchaseQuoteInitialData?.purchaseQuoteLoanTransactionTempId) {
        getDueToBcValue(purchaseQuoteInitialData);
      }
    }
  }, [purchaseQuoteInitialData]);

  return (<>
    <LmsFormGenerator
      isLoading={isPurchaseQuoteInitialDataLoading}
      myForm={myForm}
      error={error}
      actions={[
        { text: "Generate Purchase Advise", icon: GlobalConstants.Icons.SAVE, action: "save" },
        "close",
      ]} 
      handleClose={onClose}
      onFormChanged={onAddPurchaseAdviceQuoteFormChanged}
      handleSubmit={handleGeneratePurchaseAdvice}
    />
  </>);
};

const LmsServicingPurchaseAdviceQuoteView: FC<PurchaseAdviceQuoteViewProps> = ({ id }) => {
  const [{ data: adviceQuoteData, isLoading, error, fetchData }] = useFetchData(`purchasequotes/initial-data-for-edit/${id}`); 
  const [dueToBcData, setDueToBcData] = useState(undefined as any);
  const [revenueData, setRevenueData] = useState(undefined as any);
  const myForm = purchaseAdviceQuoteViewFields(
    new BCFormController({ 
      ...adviceQuoteData, 
      ...revenueData, 
      "dueToBc": dueToBcData || adviceQuoteData?.totalAmount
    }, 
    { initIsDisabled: true, values: { 
      loanComments: adviceQuoteData?.loanComments,
      loanId: adviceQuoteData?.loanId,
      purchasersTotalPurchase: adviceQuoteData?.totalAmount,
      revenueRecognitionTempId: adviceQuoteData?.revenueRecognitionTempId,
      purchaseQuoteLoanTransactionTempId: adviceQuoteData?.purchaseQuoteLoanTransactionTempId,
      buildersCapitalPercentageId: adviceQuoteData?.buildersCapitalPercentageId,
      notionalBuildersCapitalPercentageId: adviceQuoteData?.notionalBuildersCapitalPercentageId
    } }),
    adviceQuoteData?.statusOption === "New",
    false
  );
  
  useEffect(() => {
    if (adviceQuoteData) {
      if (adviceQuoteData?.purchaseQuoteLoanTransactionTempId) {
        const getDueToBcValue = async () => {
          const { data: dueToBcValue } = await fetchData(`purchasequotes/due-to-bc/${adviceQuoteData?.purchaseQuoteLoanTransactionTempId}`, { fetchMethod: "GET" });
          setDueToBcData(dueToBcValue?.dueToBc);
          FeatureFlags.IS_DEBUG && console.log("dueToBcValue", dueToBcValue);
        };
        
        getDueToBcValue();
      }

      if (adviceQuoteData?.revenueRecognitionTempId) {
        const getRevenueValue = async () => {
          const { data: revenueValue } = await fetchData(`purchasequotes/revenue-recognition-temp-data/${adviceQuoteData?.revenueRecognitionTempId}`, { fetchMethod: "GET" });
          setRevenueData(revenueValue);
          FeatureFlags.IS_DEBUG && console.log("RevenueValue", revenueValue);
        };
        
        getRevenueValue();
      }
    }
  }, [adviceQuoteData]);

  return <LmsFormGenerator myForm={myForm} error={error} actions={["close"]} isLoading={isLoading} />;
};

const LmsServicingPurchaseAdviceQuoteMainPage = () => {
  return <LmsTable
    apiSuffix="/purchasequotes/advice-quotes"
    actionButtons={[
      {
        text: "Add",
        path: "servicing-purchase-advice-quote/purchaseeligible"
      },
      "export"
    ]}
    columnLabelsDictionary={{
      saleTypeOption: "Sale Type",
      statusOption: "Status",
      recognitionOption: "Revenue Recognition Method",
    }}
  />;
};

const LmsServicingPurchaseAdviceQuote = ({ params = {} as any, history }) => {
  const allowedActions = ["add"];
  const allowedSubpages = [
    "purchaseeligible",
  ];

  const { isMainPage, isActionAllowed, isEdit, actionLower, isSubpageAllowed, id} = useParseRouteActions(params, allowedActions, allowedSubpages);
  if (!isActionAllowed) {
    history.goBack();
  }

  return (
    <>
      {isMainPage && <LmsServicingPurchaseAdviceQuoteMainPage />}
      {isEdit && <LmsServicingPurchaseAdviceQuoteView id={id} />}
      {isSubpageAllowed && <>{actionLower === "purchaseeligible" && <LmsServicingPurchaseAdviceQuotesElegibleLoan />}</>}
    </>
  );
};

export default LmsServicingPurchaseAdviceQuote;
